import { Theme } from "../../../../providers/ThemeProvider";
import { isRangeBiomarkerValue } from "../../store/helper";
import {
  BiomarkerItem,
  BiomarkerRangeFields,
  BiomarkerValueEntry,
} from "../../../../common/entities/type";

// Category ids follow enBiomarkerCategory scoring on BiomarkerItem.scoreCategory
// (1 Borderline, 2 Normal, 3 Optimal, 4 Out of range) — same mapping the score
// side panel uses (BIOMARKER_CATEGORY_LABELS in usePanelData).
export type BiomarkerBadge = {
  label: string;
  background: string;
  color: string;
};

export const getBiomarkerBadge = (
  scoreCategory: number,
  theme: Theme
): BiomarkerBadge | null => {
  switch (scoreCategory) {
    case 1:
      return {
        label: "Borderline",
        background: theme.colorTokens.W[20],
        color: theme.colorTokens.W[90],
      };
    case 2:
      return {
        label: "Normal",
        background: theme.colorTokens.P[10],
        color: theme.colorTokens.P[70],
      };
    case 3:
      return {
        label: "Optimal",
        background: theme.colorTokens.S[10],
        color: theme.colorTokens.S[80],
      };
    case 4:
      return {
        label: "Out of range",
        background: theme.colorTokens.D[10],
        color: theme.colorTokens.D[70],
      };
    default:
      return null;
  }
};

// Pointer / dot color per category — same palette the score screen uses for
// biomarker dots (getBiomarkerCategoryColor(_, _, "biomarkers")).
export const getBiomarkerPointerColor = (
  scoreCategory: number,
  theme: Theme
): string => {
  switch (scoreCategory) {
    case 1:
      return theme.colorTokens.W[70];
    case 2:
      return theme.colorTokens.P[50];
    case 3:
      return theme.colorTokens.S[60];
    case 4:
      return theme.colorTokens.D[60];
    default:
      return theme.colors_ds.NEUTRAL_BORDER_DEFAULT;
  }
};

// The current value ALWAYS comes from the biomarker itself: `value` (numeric),
// `qvalue` (qualitative) and the `range_*` bounds on the item already mirror
// its current reading. The per-entry `active` flag is never consulted — it does
// not mean "the value to display" (a reading can be newer than the active one),
// so reading it produced values that disagreed with the item.
//
// The reading history, newest first — every entry, no filtering. The Readings /
// Levels-over-time lists show all of them, matching the old Biomarkers page.
export const getAllEntries = (item: BiomarkerItem): BiomarkerValueEntry[] => {
  return [...(item.values || [])].sort(
    (a, b) =>
      new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime()
  );
};

// Newest reading — used for the "last recorded" DATE only. The displayed value
// comes from the item (see above), not from this entry.
export const getLatestEntry = (
  item: BiomarkerItem
): BiomarkerValueEntry | null => {
  const entries = getAllEntries(item);
  return entries.length > 0 ? entries[0] : null;
};

export const hasRecordedValue = (item: BiomarkerItem): boolean =>
  (item.values || []).length > 0;

// --- Change between readings ------------------------------------------------
//
// The change is computed by the backend (`item.change`) so it matches the score
// engine; the frontend never derives one. No `change` → no sparkline and no
// percentage.

// Readings that carry an actual number. Range readings ("<2.0") are stored with
// value 0 and their bounds in range_*, so plotting them would read as a drop to
// zero — the trend chart leaves them out.
export const getNumericEntries = (item: BiomarkerItem): BiomarkerValueEntry[] =>
  getAllEntries(item).filter(
    (e) =>
      !isRangeBiomarkerValue(e) && typeof e.value === "number" && !isNaN(e.value)
  );

export type ChangeInfo = {
  pct: number;
  direction: "up" | "down" | "flat";
  previousValue: number;
  previousDate: string;
};

export const getChangeInfo = (item: BiomarkerItem): ChangeInfo | null => {
  const change = item.change;
  if (item.qualitative || !change) return null;
  if (typeof change.pct !== "number" || isNaN(change.pct)) return null;
  // The backend marks a change it could not compute with an empty direction
  // and pct 0 (e.g. a previous value of 0, where no percentage exists). That
  // is not "no change" — showing "→0%" for 0 → 3.1 would be wrong — so only
  // "up"/"down" are shown.
  if (change.direction !== "up" && change.direction !== "down") return null;
  const pct = Math.round(Math.abs(change.pct));
  return {
    pct,
    direction: pct === 0 ? "flat" : change.direction,
    previousValue: change.previousValue,
    previousDate: change.previousDate,
  };
};

// The reading the backend compared against, found by its date — its own
// category colors the previous point of the sparkline.
export const getEntryAtDate = (
  item: BiomarkerItem,
  date: string
): BiomarkerValueEntry | null => {
  const target = new Date(date).getTime();
  return (
    (item.values || []).find(
      (e) => new Date(e.dateCreated).getTime() === target
    ) ?? null
  );
};

// --- Range bar sections -----------------------------------------------------
//
// One section per segment of `item.ranges`, in the backend's order (low →
// high), each as wide as the range it covers, e.g.
//
//   out of range │ borderline │  NORMAL  │ borderline │ out of range
//
// Bounds come straight from the backend; names and colors come from each
// segment's `category` via the enum. A biomarker without a valid `ranges`
// payload gets no bar at all — nothing is estimated here.

export type BiomarkerCategoryKind = "outOfRange" | "borderline" | "normal";

export type RangeSection = {
  kind: BiomarkerCategoryKind;
  category: number;
  label: string;
  // Position as fractions of the bar width, 0..1.
  start: number;
  end: number;
  color: string;
  // The design draws the active section one shade darker, at full opacity.
  activeColor: string;
  // Bounds of the section; null means open-ended (the outer out-of-range ends).
  min: number | null;
  max: number | null;
  // The stretch of the scale the section is drawn over. Equal to min/max for
  // bounded sections; open ends get a finite stretch (see buildRangeSections)
  // so they have a width and values inside them can be placed.
  scaleMin: number;
  scaleMax: number;
  // Rendered under the bar for the active section: two numbers for a bounded
  // section, one "< x" / "> x" for the open-ended ends.
  limits: RangeLimit[];
  // The section runs off that end of the bar — the connection line drops its
  // tick there so the end reads as open rather than bounded.
  openStart: boolean;
  openEnd: boolean;
};

// A limit number and the fraction of the bar width it is centered on.
export type RangeLimit = {
  text: string;
  at: number;
};

const NORMAL_CATEGORY = 2;

// enBiomarkerCategory → the bar's section kind. Blood biomarkers have no
// "optimal" segment; any other category has no section kind.
const kindForCategory = (category: number): BiomarkerCategoryKind | null => {
  switch (category) {
    case 1:
      return "borderline";
    case 2:
      return "normal";
    case 4:
      return "outOfRange";
    default:
      return null;
  }
};

// Section widths follow the real size of each range, so the bar is drawn to
// scale like the trend chart. The open-ended ends extend past the outermost
// limits by the same 15% headroom the chart uses. A very narrow range (e.g. a
// 0.0495–0.055 borderline) still gets a minimum width so it stays visible and
// hoverable; the others shrink proportionally to make room.
const OPEN_END_HEADROOM = 0.15;
const MIN_SECTION_WIDTH = 0.04;

const sectionWidths = (spans: number[]): number[] => {
  const total = spans.reduce((sum, v) => sum + v, 0);
  let widths = spans.map((v) => (total > 0 ? v / total : 1 / spans.length));
  // Lift the too-narrow sections to the minimum and take that space from the
  // rest, in proportion to their size; repeat until none is below it.
  for (let pass = 0; pass < spans.length; pass++) {
    const small = widths.map((w) => w < MIN_SECTION_WIDTH);
    if (!small.some(Boolean)) break;
    const reserved = small.filter(Boolean).length * MIN_SECTION_WIDTH;
    const restTotal = widths.reduce((sum, w, i) => (small[i] ? sum : sum + w), 0);
    widths = widths.map((w, i) =>
      small[i] ? MIN_SECTION_WIDTH : (w / restTotal) * (1 - reserved)
    );
  }
  return widths;
};

// The bar is drawn only from a payload that describes one continuous scale:
// every segment has a known category, consecutive segments share their bound,
// bounds never decrease, only the two ends are open, and there is exactly one
// normal segment. Anything else (missing ranges, min > max in the source data,
// a zero-width segment) would draw a misleading bar, so none is drawn.
export const hasValidRanges = (item: BiomarkerItem): boolean => {
  const ranges = item.ranges;
  if (!Array.isArray(ranges) || ranges.length === 0) return false;
  if (ranges.filter((r) => r.category === NORMAL_CATEGORY).length !== 1) {
    return false;
  }
  return ranges.every((r, i) => {
    if (kindForCategory(r.category) === null) return false;
    const isFirst = i === 0;
    const isLast = i === ranges.length - 1;
    if (r.min === null && !isFirst) return false;
    if (r.max === null && !isLast) return false;
    if (r.min !== null && r.max !== null && !(r.max > r.min)) return false;
    if (!isFirst && ranges[i - 1].max !== r.min) return false;
    return true;
  });
};

const categoryTokens = (kind: BiomarkerCategoryKind, theme: Theme) => {
  switch (kind) {
    case "outOfRange":
      return theme.colorTokens.D;
    case "borderline":
      return theme.colorTokens.W;
    default:
      return theme.colorTokens.P;
  }
};

// Band color for a segment behind the trend chart (same hue as the bar).
export const getRangeBandColor = (category: number, theme: Theme): string => {
  const kind = kindForCategory(category);
  return kind ? categoryTokens(kind, theme)[10] : "transparent";
};

// Hatch colors for a range value ("<2.0"), which covers a span of the bar
// instead of sitting at one point. Keyed on the reading's own category, like
// the pointer.
export const getBiomarkerHatchColors = (
  scoreCategory: number,
  theme: Theme
): { fill: string; stripe: string } => {
  switch (scoreCategory) {
    case 1:
      return { fill: theme.colorTokens.W[20], stripe: theme.colorTokens.W[30] };
    case 2:
      return { fill: theme.colorTokens.P[20], stripe: theme.colorTokens.P[30] };
    case 3:
      return { fill: theme.colorTokens.S[20], stripe: theme.colorTokens.S[30] };
    case 4:
      return { fill: theme.colorTokens.D[20], stripe: theme.colorTokens.D[30] };
    default:
      // No category scored for this reading — neutral, but dark enough to read
      // as a hatch rather than an empty bar.
      return { fill: theme.colorTokens.N[50], stripe: theme.colorTokens.N[60] };
  }
};

// Limits are rendered centered on the boundary they describe (design
// 16330:65608), so each one carries the fraction of the bar it sits over.
const limitsFor = (
  min: number | null,
  max: number | null,
  start: number,
  end: number
): RangeLimit[] => {
  if (min === null && max === null) return [];
  // Open-ended ends have one bound; it is the one facing the rest of the bar.
  if (min === null) return [{ text: `< ${max}`, at: end }];
  if (max === null) return [{ text: `> ${min}`, at: start }];
  return [
    { text: `${min}`, at: start },
    { text: `${max}`, at: end },
  ];
};

// Callers must check hasValidRanges first; an invalid payload yields no
// sections.
export const buildRangeSections = (
  theme: Theme,
  item: BiomarkerItem
): RangeSection[] => {
  if (!hasValidRanges(item)) return [];
  const ranges = item.ranges!;
  const kinds = ranges.map((r) => kindForCategory(r.category)!);

  // Finite stretch of the scale each section covers. Open ends reach past the
  // outermost limits by the headroom, never below 0 for a positive scale.
  const limits = ranges
    .flatMap((r) => [r.min, r.max])
    .filter((v): v is number => v !== null);
  const lowLimit = Math.min(...limits);
  const highLimit = Math.max(...limits);
  const headroom =
    (highLimit - lowLimit) * OPEN_END_HEADROOM ||
    Math.abs(highLimit) * OPEN_END_HEADROOM ||
    1;
  const scaleFloor =
    lowLimit >= 0 ? Math.max(0, lowLimit - headroom) : lowLimit - headroom;
  const scaleCeil = highLimit + headroom;
  const scales = ranges.map((r) => ({
    min: r.min ?? scaleFloor,
    max: r.max ?? scaleCeil,
  }));
  const widths = sectionWidths(scales.map((sc) => Math.max(0, sc.max - sc.min)));
  let cursor = 0;

  return ranges.map((range, idx) => {
    const kind = kinds[idx];
    const start = cursor;
    cursor += widths[idx];
    const end = idx === ranges.length - 1 ? 1 : cursor;
    return {
      kind,
      category: range.category,
      label: getBiomarkerBadge(range.category, theme)?.label ?? "",
      start,
      end,
      color: categoryTokens(kind, theme)[20],
      activeColor: categoryTokens(kind, theme)[30],
      min: range.min,
      max: range.max,
      scaleMin: scales[idx].min,
      scaleMax: scales[idx].max,
      limits: limitsFor(range.min, range.max, start, end),
      openStart: range.min === null,
      openEnd: range.max === null,
    };
  });
};

// The normal section — the best-case scenario, and the one shown when the
// biomarker has no value to place on the bar.
export const getNormalSectionIndex = (sections: RangeSection[]): number => {
  const idx = sections.findIndex((s) => s.category === NORMAL_CATEGORY);
  return idx === -1 ? 0 : idx;
};

// Section a number falls into. Shared bounds fall to the inner section, so a
// value sitting exactly on the normal range's limits reads as normal.
const sectionIndexForValue = (
  sections: RangeSection[],
  value: number
): number => {
  const centreIndex = getNormalSectionIndex(sections);
  const centre = sections[centreIndex];
  if (value >= (centre.min ?? -Infinity) && value <= (centre.max ?? Infinity)) {
    return centreIndex;
  }
  if (value < (centre.min ?? -Infinity)) {
    for (let i = centreIndex - 1; i >= 0; i--) {
      if (value >= (sections[i].min ?? -Infinity)) return i;
    }
    return 0;
  }
  for (let i = centreIndex + 1; i < sections.length; i++) {
    if (value <= (sections[i].max ?? Infinity)) return i;
  }
  return sections.length - 1;
};

// Where a number sits on the bar, as a fraction of its width — interpolated
// inside its own section's stretch of the scale. A value beyond an open end's
// headroom is clamped to the outer edge of the bar.
export const positionForValue = (
  sections: RangeSection[],
  value: number
): number => {
  const section = sections[sectionIndexForValue(sections, value)];
  const { scaleMin, scaleMax } = section;
  const t =
    scaleMax > scaleMin
      ? Math.min(1, Math.max(0, (value - scaleMin) / (scaleMax - scaleMin)))
      : 0.5;
  return section.start + t * (section.end - section.start);
};

// Section the current value falls into, or null when there is nothing to place:
// no value, no valid ranges, or a value that is not a single number
// (qualitative, or a range like "<2.0" — that one covers a span of the bar, see
// getRangeValueSpan, and leaves the normal section highlighted).
export const getValueSectionIndex = (
  sections: RangeSection[],
  item: BiomarkerItem
): number | null => {
  if (
    item.qualitative ||
    isRangeBiomarkerValue(item) ||
    !hasRecordedValue(item) ||
    sections.length === 0
  ) {
    return null;
  }
  const value = item.value;
  if (typeof value !== "number" || isNaN(value)) return null;
  return sectionIndexForValue(sections, value);
};

// Pointer position as a fraction of the bar width (0..1), or null when there is
// nothing to point at. The value is interpolated inside its own section, so the
// pointer always sits under the section that drives the highlighted category.
export const getPointerPosition = (
  sections: RangeSection[],
  item: BiomarkerItem
): number | null => {
  if (item.qualitative || isRangeBiomarkerValue(item)) return null;
  if (!hasRecordedValue(item) || sections.length === 0) return null;
  const value = item.value;
  if (typeof value !== "number" || isNaN(value)) return null;
  return positionForValue(sections, value);
};

// A range value ("<2.0", "≥3 ≤4") is an interval, not a point: it covers a span
// of the bar, drawn hatched with a bracket over it instead of a pointer
// (design 16333:62024). Returns the span as fractions of the bar width, with
// the ends that run to infinity flagged so their tick can be dropped.
export type RangeValueSpan = {
  from: number;
  to: number;
  openStart: boolean;
  openEnd: boolean;
};

// Which side of the value a bound sits on is decided by its OPERATOR, not by
// the field carrying it: the backend stores "<2" as range_min_op "<" with
// range_min_value 2, so the field name says nothing.
const isUpperOp = (op?: string): boolean =>
  op === "<" || op === "<=" || op === "≤";
const isLowerOp = (op?: string): boolean =>
  op === ">" || op === ">=" || op === "≥";

/**
 * A range reading's own bounds ("<1" → upper 1; ">5" → lower 5; both for
 * "a–b"), read from the operators like `getRangeValueSpan`. Null for a
 * reading that isn't a range or has no usable bound.
 */
export const getRangeBounds = (
  entry: BiomarkerRangeFields
): { lower: number | null; upper: number | null } | null => {
  if (!isRangeBiomarkerValue(entry)) return null;
  let lower: number | null = null;
  let upper: number | null = null;
  [
    { op: entry.range_min_op, value: entry.range_min_value },
    { op: entry.range_max_op, value: entry.range_max_value },
  ].forEach(({ op, value }) => {
    if (!op || typeof value !== "number") return;
    if (isUpperOp(op)) upper = value;
    else if (isLowerOp(op)) lower = value;
  });
  return lower === null && upper === null ? null : { lower, upper };
};

// Readings the trend chart can place: plain numbers, plus range readings
// with a usable bound (drawn as a hatched span). Qualitative ones never.
export const getChartEntries = (item: BiomarkerItem): BiomarkerValueEntry[] =>
  item.qualitative
    ? []
    : getAllEntries(item).filter((e) =>
        isRangeBiomarkerValue(e)
          ? getRangeBounds(e) !== null
          : typeof e.value === "number" && !isNaN(e.value)
      );

export const getRangeValueSpan = (
  sections: RangeSection[],
  item: BiomarkerItem
): RangeValueSpan | null => {
  if (item.qualitative || !isRangeBiomarkerValue(item)) return null;
  if (!hasRecordedValue(item) || sections.length === 0) return null;

  let lower: number | null = null;
  let upper: number | null = null;
  const bounds: { op?: string; value?: number }[] = [
    { op: item.range_min_op, value: item.range_min_value },
    { op: item.range_max_op, value: item.range_max_value },
  ];
  bounds.forEach(({ op, value }) => {
    if (!op || typeof value !== "number") return;
    if (isUpperOp(op)) upper = value;
    else if (isLowerOp(op)) lower = value;
  });
  if (lower === null && upper === null) return null;

  return {
    from: lower === null ? 0 : positionForValue(sections, lower),
    to: upper === null ? 1 : positionForValue(sections, upper),
    openStart: lower === null,
    openEnd: upper === null,
  };
};
